import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import type { CFMonthlyData } from '../../types/campaignFinance';
import { fmtMoney } from '../../utils/campaignFinanceUtils';

interface CFTrendChartProps {
  data: CFMonthlyData[];
  title?: string;
  height?: number;
  className?: string;
}

const GRID_COLOR = 'var(--cf-grid-alt)';   // background.secondary
const AXIS_COLOR = 'var(--cf-axis)';   // accent-400
const SERIES_COLOR = 'var(--cf-out-deep)'; // primary-800

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="card p-2 shadow-lg">
      <div className="text-xs font-semibold text-text-primary dark:text-text-dark-primary mb-1">{label}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-text-secondary dark:text-text-dark-secondary">{p.name}:</span>
          <span className="font-semibold text-text-primary dark:text-text-dark-primary tabular-nums">{fmtMoney(p.value, { compact: true })}</span>
        </div>
      ))}
    </div>
  );
};

const CFTrendChart: React.FC<CFTrendChartProps> = ({ data, title, height = 180, className = '' }) => {
  const chartData = data.map(d => ({ month: d.month, total: Number(d.total) || 0 }));

  if (chartData.length === 0) {
    return (
      <div className={`card flex items-center justify-center ${className}`} style={!className.includes('h-') ? { minHeight: height } : undefined}>
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">No trend data available</p>
      </div>
    );
  }

  return (
    <div className={`card ${className}`}>
      {title && (
        <div className="text-xs font-semibold text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider mb-3">{title}</div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 11, fill: AXIS_COLOR }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: AXIS_COLOR }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: number) => fmtMoney(v, { compact: true })}
            width={55}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="total"
            name="Total"
            stroke={SERIES_COLOR}
            fill={SERIES_COLOR}
            fillOpacity={0.15}
            strokeWidth={2}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CFTrendChart;
